// Central place for REST API endpoint path segments.
// NOTE: axios baseURL already includes `/api`, so these should NOT start with a leading slash.

export const authEndpoint = "auth";
export const userProfileEndpoint = "users/me/profile";
export const userPushStatusEndpoint = "users/me/push-status";
export const notificationsMeEndpoint = "notifications/me";
export const notificationsUnreadCountEndpoint = "notifications/me/unread-count";
export const notificationsReadAllEndpoint = "notifications/me/read-all";
export const notificationReadEndpoint = (id: number | string) =>
  `notifications/${id}/read`;

export const userDeviceTokenEndpoint = "users/me/device-token";
export const usersEndpoint = "users";
export const userByIdEndpoint = (id: number | string) => `users/${id}`;
export const createCustomerEndpoint = "users/customers";

export const staffEndpoint = "staff";
export const staffInviteEndpoint = "staff/invite";
export const staffPromoteEndpoint = "staff/promote";
export const staffDemoteEndpoint = "staff/demote";
export const staffByIdEndpoint = (id: number | string) => `staff/${id}`;

export const reportsExportEndpoint = (
  reportKey: string,
  format: "csv" | "xlsx" | "pdf",
) => `reports/${reportKey}/export?format=${format}`;
export const reportsDataEndpoint = (reportKey: string) =>
  `reports/${reportKey}/data`;
export const dashboardOverviewEndpoint = "dashboard/overview";

// Services

export const servicesEndpoint = "services";
export const serviceByIdEndpoint = (id: number | string) => `services/${id}`;
export const serviceBySlugEndpoint = (slug: string) => `services/by-slug/${slug}`;
export const serviceHardDeleteEndpoint = (id: number | string) => `services/${id}/hard`;
export const serviceRestoreEndpoint = (id: number | string) => `services/${id}/restore`;
export const serviceDeletionEligibilityEndpoint = (id: number | string) =>
  `services/${id}/deletion-eligibility`;

// Service categories

export const serviceCategoriesEndpoint = "service-categories";
export const serviceCategoryByIdEndpoint = (id: number | string) =>
  `service-categories/${id}`;
export const serviceCategoriesReorderEndpoint = "service-categories/reorder";
export const servicesReorderEndpoint = "services/reorder";

// Appointments

export const appointmentsEndpoint = "appointments";
export const appointmentAvailableSlotsEndpoint = "appointments/available-slots";
export const appointmentCheckAvailabilityEndpoint = "appointments/check-availability";
export const appointmentByIdEndpoint = (id: number | string) => `appointments/${id}`;
export const appointmentCancelEndpoint = (id: number | string) => `appointments/${id}/cancel`;
export const appointmentStatusEndpoint = (id: number | string) => `appointments/${id}/status`;
export const appointmentApproveEndpoint = (id: number | string) => `appointments/${id}/approve`;
export const appointmentGroupCheckAvailabilityEndpoint =
  "appointments/group/check-availability";
export const appointmentGroupEndpoint = "appointments/group";
export const appointmentGroupsEndpoint = "appointments/groups";
export const appointmentGroupByIdEndpoint = (id: number | string) =>
  `appointments/group/${id}`;
export const appointmentGroupCancelEndpoint = (id: number | string) =>
  `appointments/group/${id}/cancel`;
export const appointmentAdjustmentsEndpoint = (id: number | string) =>
  `appointments/${id}/adjustments`;
export const appointmentDiscountAdjustmentEndpoint = (id: number | string) =>
  `appointments/${id}/adjustments/discount`;
export const appointmentSurchargeAdjustmentEndpoint = (id: number | string) =>
  `appointments/${id}/adjustments/surcharge`;
export const appointmentGoodwillAdjustmentEndpoint = (id: number | string) =>
  `appointments/${id}/adjustments/goodwill`;
export const appointmentReverseAdjustmentEndpoint = (
  id: number | string,
  adjId: number | string,
) => `appointments/${id}/adjustments/${adjId}/reverse`;
export const appointmentOverrideTotalEndpoint = (id: number | string) =>
  `appointments/${id}/override-total`;

// Discounts & Surcharges

export const discountsEndpoint = "discounts";
export const discountByIdEndpoint = (id: number | string) => `discounts/${id}`;
export const discountValidateEndpoint = "discounts/validate";

export const surchargesEndpoint = "surcharges";
export const surchargeByIdEndpoint = (id: number | string) => `surcharges/${id}`;

// Hair records

export const hairRecordsEndpoint = "hair-records";
export const myHairRecordsEndpoint = "hair-records/me";
export const customerHairRecordsEndpoint = (customerUserId: number | string) =>
  `hair-records/customer/${customerUserId}`;

// Financials

export const financialsEndpoints = {
  transactions: "financials/transactions",
  accounts: "financials/accounts",
  ledger: "financials/ledger",
  ledgerBalance: (accountId: number | string) => `financials/ledger/balance/${accountId}`,
  journalEntries: "financials/journal-entries",
  journalEntryById: (id: number | string) => `financials/journal-entries/${id}`,
  journalEntryPost: (id: number | string) => `financials/journal-entries/${id}/post`,
  income: "financials/income",
  expenses: "financials/expenses",
  reports: {
    trialBalance: "financials/reports/trial-balance",
    balanceSheet: "financials/reports/balance-sheet",
    incomeStatement: "financials/reports/income-statement",
    taxSummary: "financials/reports/tax-summary",
  },
} as const;

// Wallet

export const walletEndpoint = "wallet";
export const walletMeEndpoint = "wallet/me";
export const walletMeTransactionsEndpoint = "wallet/me/transactions";
export const walletMeWithdrawalsEndpoint = "wallet/me/withdrawals";
export const walletWithdrawalsListEndpoint = "wallet/withdrawals/list";
export const walletUserTransactionsEndpoint = (userId: number | string) =>
  `wallet/${userId}/transactions`;
export const walletUserCreditEndpoint = (userId: number | string) =>
  `wallet/${userId}/credit`;
export const walletUserDebitEndpoint = (userId: number | string) =>
  `wallet/${userId}/debit`;
export const walletWithdrawalByIdEndpoint = (id: number | string) =>
  `wallet/withdrawals/${id}`;

// Settings

export const settingsEndpoints = {
  general: "settings/general",
  email: "settings/email",
  payment: "settings/payment",
  paymentPublic: "settings/payment/public",
  notification: "settings/notification",
  financial: "settings/financial",
  pricing: "settings/pricing",
  geoLocation: "settings/geo-location",
  sms: "settings/sms",
  business: "settings/business",
} as const;

// Location

export const locationEndpoint = "location";
export const locationByIdEndpoint = (id: number | string) => `location/${id}`;

// Mails

export const mailEndpoints = {
  templates: "mails/templates",
  logs: "mails/logs",
  templatesUpdate: "mails/templates/update",
  test: "mails/test",
} as const;

// Paystack

export const paystackWebhookEndpoint = "paystack/webhook";
export const paystackInitiateCardPaymentEndpoint = "paystack/initiate-card-payment";
export const paystackInitiateBankTransferEndpoint = "paystack/initiate-bank-transfer";
export const paystackQuoteEndpoint = "paystack/quote";
export const paystackVerifyEndpoint = "paystack/verify";

// Content

export const contentEndpoints = {
  articles: "content/articles",
  articleBySlug: (slug: string) => `content/articles/${encodeURIComponent(slug)}`,
  testimonials: "content/testimonials",
  adminItems: "content/admin/items",
  adminItemById: (id: number | string) => `content/admin/items/${id}`,
} as const;

// Uploads

export const uploadsEndpoint = "uploads";
export const uploadByIdEndpoint = (id: number | string) => `uploads/${id}`;

// Business data backup

export const dataBackupEndpoints = {
  regions: "data-backup/regions",
  export: "data-backup/export",
  import: "data-backup/import",
} as const;
